/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    afterNextRender,
    ChangeDetectorRef,
    Component,
    HostListener,
    inject,
    OnInit,
    signal,
    ViewRef
} from '@angular/core';
import {KtsComponent} from '../kts/kts.component';
import {ProgramRun} from '../../model/programRun';
import {ProgramTiming} from '../../model/programTiming';
import {CommonHelperService} from '../../service/common-helper.service';
import {Observable} from 'rxjs';
import {ColDef, ColGroupDef, GridApi, GridReadyEvent} from 'ag-grid-community';

@Component({
    selector: 'app-program-runs',
    templateUrl: './program-runs.component.html',
    styleUrls: ['./program-runs.component.scss'],
    standalone: false
})
export class ProgramRunsComponent extends KtsComponent implements OnInit {
    private readonly cdr = inject(ChangeDetectorRef);
    readonly viewLoading = signal(false);
    programRuns = new Map<string, Array<ProgramRun>>();
    selectedProgramRuns = new Array<ProgramRun>;
    selectedProgramRunsToDelete = new Array<ProgramRun>;
    permissionUrl = '/esciUI/ktsProgramRunsPermissions';
    adminPermissionUrl = '/esciUI/ktsEditorPermission';
    adminOnlyColumns = ['archived'];
    fileNamePrefix = 'Saved ';
    deleteModalApi: GridApi;
    exportOptions = [
        {
            label: 'Export ICDe Dates',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("ICD");
            }
        },
        {
            label: 'Export WHA Dates',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("WHA");
            }
        },
        {
            label: 'Export Complexity Package',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("MRD");
            }
        },
        {
            label: 'Export Batch Release Dates',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("BatchRelease");
            }
        },
        {
            label: 'Export eSOR Dates',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("eSOR");
            }
        },
        {
            label: 'Export Combined Report',
            icon: 'pi pi-download',
            command: () => {
                this.exportAllTiming("Combined");
            }
        }];
    ecsExportOptions = this.exportOptions.filter(option =>
        option.label.includes('ICDe')
        || option.label.includes('WHA')
        || option.label.includes('eSOR'));
    showTimingSelectionModal: boolean;
    showTimingDeletionModal: boolean;

    showSelectTimingsPrompt(): boolean {
        const savedRuns = this.programRuns.get(this.type);
        const openedVersions = this.ktsTimingTemplateRowMap.get(this.type)?.size ?? 0;
        return this.permission()
            && !this.loading
            && !this.viewLoading()
            && !this.exporting
            && !!savedRuns?.length
            && openedVersions === 0;
    }

    ngOnInit() {
        this.permission.set(false);
        this.esciService.getPermissionAndFileSizeLimit(this, false);
    }

    override onAdminPermissionResolved(): void {
        this.loadProgramRuns();
    }

    loadProgramRuns(): void {
        this.programRuns = this.esciService.getProgramRuns(this, null, false, this.adminPermission(), () => {
            this.loading = false;
        });
    }

    afterProgramRunsLoaded(updatedProgramRuns: Map<string, Array<ProgramRun>>): void {
        const viewRef = this.cdr as ViewRef;
        if (viewRef.destroyed) {
            return;
        }
        this.programRuns = new Map(updatedProgramRuns);
        this.loading = false;
        this.cdr.detectChanges();
    }

    showProgramTiming(programRuns: ProgramRun[]) {
        const programRunIdsToHydrate = programRuns
            .filter(pr => !pr.programTimingRows || pr.programTimingRows.length === 0)
            .map(pr => pr.id);
        this.viewLoading.set(true);
        this.esciService.getProgramRunDetails(this, programRunIdsToHydrate, {
            includeArchived: this.adminPermission(),
            onComplete: () => {
            // Only build timing for runs that actually have timing rows. Seeding buildProgramTiming with runs that
            // still have no rows after hydrate (no timing rows, or a partial/failed hydrate) would create empty
            // template-version entries and lead to runtime errors on later selections that assume rows exist.
            const runsWithTiming = programRuns.filter(pr => pr.programTimingRows && pr.programTimingRows.length > 0);
            const skippedRuns = programRuns.filter(pr => !pr.programTimingRows || pr.programTimingRows.length === 0);
            if (skippedRuns.length > 0) {
                this.messageService.processError('No timing data found for: '
                    + skippedRuns.map(pr => pr.programName).join(', ') + '. These programs were skipped.');
            }
            this.buildProgramTiming(runsWithTiming);
            this.viewLoading.set(false);
            const viewRef = this.cdr as ViewRef;
            if (!viewRef.destroyed) {
                this.cdr.detectChanges();
            }
            }
        });
    }

    mergeProgramRunDetails(results: any[]): void {
        const programById = new Map<number, ProgramRun>();
        this.programRuns.forEach((list) => list.forEach(pr => programById.set(pr.id, pr)));
        for (const result of results) {
            const existingProgramRun = programById.get(result.id);
            if (existingProgramRun) {
                existingProgramRun.setProgramRunFromEsciData(result);
            }
        }
    }

    private buildProgramTiming(programRuns: ProgramRun[]) {
        for (const program of programRuns) {
            // Convert the selectedProgramRuns into a list of ktsTimingTemplateRows and add it to the map that shows the grids
            if (!this.ktsTimingTemplateRowMap.get(this.type)) {
                this.ktsTimingTemplateRowMap.set(this.type, new Map());
            }
            let templateRows = this.ktsTimingTemplateRowMap.get(this.type).get(program.templateVersion);
            const programName = this.type === 'ECS'
                ? this.getGridProgramKeyFromProgramRun(program)
                : program.programName;
            let newVersion = false;
            if (!templateRows) {
                templateRows = [];
                newVersion = true;
            } else {
                // check if this program name has already been selected
                const isDuplicateProgram = [...templateRows[0].programData.keys()].some((value) => value === programName);
                if (isDuplicateProgram) {
                    continue;
                }
            }
            for (const row of program.programTimingRows) {
                const programTiming = new ProgramTiming();
                programTiming.setDataFromProgramTimingRow(row);
                // kts row will either already exist or we will pull it from our program run data
                let ktsRow = null;
                if (newVersion) {
                    // we have the full template row from the db, but we need to set the program data now
                    ktsRow = row.ktsTimingTemplateRow;
                    ktsRow.programData.set(programName, programTiming);
                    templateRows.push(ktsRow);
                } else {
                    // we have the template row from the map, but we need to set the program data now
                    ktsRow = templateRows.find(r => r.id === row.ktsTimingTemplateRow.id);
                }
                if (!ktsRow) {
                    // this should never happen, but throw an error message
                    this.messageService.processError('Could not find the ktsTimingTemplateRow for the programTimingRow');
                }
                ktsRow.programData.set(programName, programTiming);
            }
            this.version = program.templateVersion;
            // sort template rows by kts timing template row number
            templateRows.sort((a, b) => a.rowNumber - b.rowNumber);
            this.ktsTimingTemplateRowMap.get(this.type).set(program.templateVersion, templateRows);
            if (!this.fullCols.get(this.type)) {
                this.fullCols.set(this.type, new Map());
            }
            if (!this.fullCols.get(this.type).get(this.version) || this.fullCols.get(this.type).get(this.version).length === 0) {
                this.fullCols.get(this.type).set(this.version, structuredClone(this.cols));
            }
            this.programClassificationMap.set(programName, program.vehicleProjectClassification);
            const programDateColumn = this.getProgramDateColumn(programName, program.vehicleProjectClassification);
            programDateColumn.cellRendererParams = {disableButtons: true};
            if (this.type === 'ECS') {
                const rpo = program.gpsRpo ? ` (${program.gpsRpo})` : '';
                programDateColumn.headerValueGetter = () => `${program.programName}${rpo}`;
            }
            this.fullCols.get(this.type).get(this.version).push(programDateColumn);
            this.refreshGrid();
        }

        // remove from the grid the no longer selected programs
        const programNamesByVersion = new Map();
        programRuns.forEach(p => {
            const programNames = programNamesByVersion.get(p.templateVersion) || [];
            programNames.push(this.type === 'ECS' ? this.getGridProgramKeyFromProgramRun(p) : p.programName);
            programNamesByVersion.set(p.templateVersion, programNames);
        });

        // Iterate over each template version and remove unselected programs
        programNamesByVersion.forEach((programNamesForThisVersion, version) => {
            const rowsForVersion = this.ktsTimingTemplateRowMap.get(this.type)?.get(version) || [];
            rowsForVersion.forEach(row => {
                const programNameFromModal = row.programData.keys().next().value;
                // If the program name is still selected, skip to the next iteration
                if (programNamesForThisVersion.includes(programNameFromModal)) {
                    return;
                }
                // Remove program from programData if not selected
                row.programData.delete(programNameFromModal);
                // Find the program column by header name and remove it
                const fullColsForVersion = this.fullCols.get(this.type).get(version) || [];
                const index = fullColsForVersion.findIndex(col => col.headerName === programNameFromModal);
                if (index >= 0) {
                    fullColsForVersion.splice(index, 1);
                }
            });
        });

        this.showTimingSelectionModal = null;
        this.handleTabClick(this.version);
        this.selectedMomentumProgram = null;
        this.setDurationBooleans();
        this.setupGantt();
    }

    clearProgramRunsFromGrid() {
        this.clearModalSelection(this.modalGridApi);
        this.clearModalSelection(this.deleteModalApi);
        this.ktsTimingTemplateRowMap.clear();
        this.fullCols.clear();
        this.gridApiMap.clear();
        this.setActiveTabIndex(0);
        this.version = null;
        this.refreshGrid();
    }

    exportAllTiming(type: string) {
        if (this.type !== 'PROD' && this.type !== 'ECS') {
            return;
        }
        if (this.type === 'ECS' && type !== 'ICD' && type !== 'WHA' && type !== 'eSOR') {
            return;
        }
        const selectedRuns = this.selectedRunsForExport();
        const programs = selectedRuns.length > 0 ? selectedRuns : (this.programRuns.get(this.type) ?? []);
        if (type !== 'ICD' && programs.length === 0) {
            this.messageService.processError('No programs available to export.');
            return;
        }
        if (type === 'ICD') {
            // ICD report is different from the other reports in that we either export selected or we pull a file from db
            // so the parameter here will always be selected programs. if empty, backend will pull file from db

            // need to know if user is clicking button from starting page or modal though
            // if there is nothing in the kts map, then they haven't started viewing the programs, so send none
            // unless they are coming from the modal, then we need to send the selected programs
            let programsForIcd = [];
            if (selectedRuns.length) {
                // even though they have selected programs, only send if they are in view map or modal is open
                if (this.ktsTimingTemplateRowMap.get(this.type)?.size
                    || this.showTimingSelectionModal
                    || this.showTimingDeletionModal) {
                    programsForIcd = selectedRuns;
                }
            } else if (this.type === 'ECS') {
                programsForIcd = this.programRuns.get(this.type) ?? [];
            }
            this.exportReportXlsx(this.downloadService.exportAllIcdDatesReport.bind(this.downloadService, programsForIcd.map(o => o.id)), 'All ICDe Dates Report');
        } else if (type === 'WHA') {
            this.exportReportXlsx(this.downloadService.exportAllWhaTiming.bind(this.downloadService, programs.map(o => o.id)), 'All WHA Dates Report');
        } else if (type === 'MRD') {
            this.exportReportXlsx(this.downloadService.exportAllMrdTiming.bind(this.downloadService, programs.map(o => o.id)), 'All MRD Complexity Package Dates Report');
        } else if (type === 'BatchRelease') {
            this.exportReportXlsx(this.downloadService.exportAllBatchReleaseDatesReport.bind(this.downloadService, programs.map(o => o.id)), 'All Batch Release Dates Report');
        } else if (type === 'eSOR') {
            this.exportReportXlsx(this.downloadService.exportAllEsorDatesReport.bind(this.downloadService, programs.map(o => o.id)), 'All eSOR Dates Report');
        } else if (type === 'Combined') {
            this.exportReportXlsx(this.downloadService.exportAllCombinedDatesReport.bind(this.downloadService, programs.map(o => o.id)), 'All Combined Dates Report');
        }
        this.showTimingSelectionModal = false;
    }

    columnDefs: ColDef[] = [
        {
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            width: 50,
            filter: false
        },
        {
            field: 'programName',
            headerName: 'Program Name',
            minWidth: 180,
            maxWidth: 400,
            valueGetter: (params) => this.formatProgramDisplayName(
                params.data?.programName,
                params.data?.vehicleProjectClassification
            )
        },
        {field: 'templateVersion', headerName: 'Template Version', minWidth: 140, maxWidth: 400},
        {
            field: 'archived',
            headerName: 'Archived',
            minWidth: 90,
            maxWidth: 120,
            valueFormatter: (params) => params.data?.archived ? 'Yes' : 'No'
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            minWidth: 120,
            maxWidth: 400,
            valueFormatter: (params) => CommonHelperService.formatDate(params.data?.createdDate),
            comparator: (valueA, valueB, nodeA, nodeB) => {
                const timeA = CommonHelperService.toEpochMillis(nodeA?.data?.createdDate);
                const timeB = CommonHelperService.toEpochMillis(nodeB?.data?.createdDate);
                return timeA - timeB;
            }
        },
        {field: 'createdBy', headerName: 'Created By', minWidth: 140, maxWidth: 400},
    ];

    get selectModalStyle(): {height: string} {
        return {height: '100%'};
    }

    get deleteModalStyle(): {width: string; maxHeight: string} {
        return {width: '70vw', maxHeight: '90vh'};
    }

    get selectModalStyleClass(): string {
        return this.type === 'ECS' ? 'ecs-select-timing-dialog' : 'select-timing-dialog';
    }

    isViewSavedTypeSelectorDisabled(): boolean {
        if (this.exporting) {
            return true;
        }
        return !this.types.some(t => (this.programRuns.get(t)?.length ?? 0) > 0);
    }

    get visibleExportOptions() {
        if (this.type === 'PROD') {
            return this.exportOptions;
        }
        if (this.type === 'ECS') {
            return this.ecsExportOptions;
        }
        return [];
    }

    get selectColumnDefs(): (ColDef | ColGroupDef)[] {
        if (this.type !== 'ECS') {
            return this.columnDefs;
        }
        return [
            ...this.columnDefs,
            {
                field: 'gpsRpo',
                headerName: 'GPS RPO',
                minWidth: 80,
                maxWidth: 220
            }
        ];
    }

    selectedRunsForExport(): ProgramRun[] {
        if (this.showTimingDeletionModal && this.selectedProgramRunsToDelete?.length) {
            return this.selectedProgramRunsToDelete;
        }
        return this.selectedProgramRuns || [];
    }

    onSelectionChanged() {
        this.selectedProgramRuns = this.modalGridApi.getSelectedRows(); // Get selected rows
    }

    onDeleteSelectionChanged() {
        this.selectedProgramRunsToDelete = this.deleteModalApi.getSelectedRows(); // Get selected rows
    }

    private reportDownloadError(error: {error?: Blob | string}): void {
        const fallback = 'Error generating report. Please try again later.';
        const body = error?.error;
        if (body instanceof Blob) {
            this.readBlobText(body).then(text => {
                this.messageService.processError(text?.trim() || fallback);
                this.exporting = false;
            }).catch(() => {
                this.messageService.processError(fallback);
                this.exporting = false;
            });
            return;
        }
        this.messageService.processError(typeof body === 'string' && body.trim() ? body : fallback);
        this.exporting = false;
    }

    private readBlobText(body: Blob): Promise<string> {
        if (typeof body.text === 'function') {
            return body.text();
        }
        return new Response(body).text();
    }

    private exportReportXlsx(serviceMethod: () => Observable<Blob>, fileNamePrefix: string) {
        this.exporting = true;
        serviceMethod().subscribe({
            next: (blob) => {
                // account for error response
                const timestamp = CommonHelperService.getTimeStampForReport();
                const fileName = `${fileNamePrefix} ${timestamp}.xlsx`;
                CommonHelperService.downloadFile(blob, fileName);
                this.messageService.processSuccess('Report successfully generated. Please see the downloaded file.');
                this.exporting = false;
            },
            error: (error) => {
                this.reportDownloadError(error);
            }
        });
    }

    @HostListener('window:resize')
    onResize() {
        if (this.deleteModalApi && !this.deleteModalApi.isDestroyed()) {
            this.deleteModalApi.sizeColumnsToFit();
        }
        if (this.modalGridApi && !this.modalGridApi.isDestroyed()) {
            this.modalGridApi.sizeColumnsToFit();
        }
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.sizeColumnsToFit();
        }
    }

    clearModalSelection(gridApi: GridApi) {
        if (!gridApi || gridApi.isDestroyed()) {
            return;
        }
        //deselect all rows in the modalGridApi
        gridApi.deselectAll();
        // also clear filters as they are changing between different types
        gridApi.setFilterModel(null);
        this.onSelectionChanged();
    }

    deleteProgramRuns(programRuns: ProgramRun[]) {
        // popup a confirmation message to user to confirm before deleting
        this.confirmationService.confirm({
            message: `You are about to delete ${programRuns.length} program timing${programRuns.length === 1 ? '' : 's'}. This action is permanent and cannot be undone. Are you sure you want to proceed?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Delete',
            rejectLabel: 'No, Cancel',
            acceptButtonProps: { severity: 'danger' },
            accept: () => {
                this.esciService.deleteProgramRuns(this, programRuns, () => {
                    this.loading = true;
                    this.clearModalSelection(this.deleteModalApi);
                    this.showTimingDeletionModal = null;
                    this.loadProgramRuns();
                });
            },
            reject: () => {
                // User clicked cancel, do nothing
            }
        });
    }
    onDeleteModalGridReady(params: GridReadyEvent) {
        this.deleteModalApi = params.api;
        afterNextRender(() => {
            if (this.deleteModalApi && !this.deleteModalApi.isDestroyed()) {
                this.deleteModalApi.sizeColumnsToFit();
                this.setModalAdminColumnsVisible(this.deleteModalApi);
            }
        }, { injector: this.injector });
    }

    override onModalGridReady(params: GridReadyEvent) {
        super.onModalGridReady(params);
        afterNextRender(() => {
            this.setModalAdminColumnsVisible(this.modalGridApi);
        }, { injector: this.injector });
    }

    private setModalAdminColumnsVisible(gridApi: GridApi): void {
        if (this.isGridApiAlive(gridApi)) {
            gridApi.setColumnsVisible(this.adminOnlyColumns, !!this.adminPermission());
        }
    }
}
